import React, { useEffect, useState } from "react";
import { tourStepsResources } from "./tourStepsResources";
import GuidedTour from "../../components/GuidedTour";
import BubbleText from "../../components/BubbleText";

/**
 * @fileoverview Educational resources and help documentation component.
 *
 * @component Resources
 * @description Provides access to FAQs, instructional videos, and learning resources.
 *
 * Features:
 * - FAQ section with common questions
 * - Instructional video placeholders (coming soon) until real videos exist
 * - Loading states
 * - Theme-aware styling
 * - Guided tour integration
 */

interface FAQ {
  question: string;
  answer: string;
}

interface InstructionalVideo {
  title: string;
  description: string;
  /** Present when a real video is ready; omit for coming-soon placeholders */
  videoUrl?: string;
}

const FAQS: FAQ[] = [
  {
    question: "What is Race The Clock?",
    answer:
      "Race The Clock is a practice tool for recognition and recall. You build a collection of items, then show them one at a time at a speed you choose. It started as a way to help students reach 60 letters a minute, and it now works for letters, numbers, math facts, vocabulary, and more.",
  },
  {
    question: "How do I track student progress?",
    answer:
      "After a session finishes, a report is saved with items shown, time taken, missed items, and skipped items. Open Reports to see those sessions, charts by collection, and how practice changes over time. Older sessions from before this tracking may show zeros for time, missed, and skipped.",
  },
  {
    question: "How do I create a collection?",
    answer:
      "Go to Your Collections and use the dashed card to start a new one. Name it, pick a category, then generate items or add your own. Save it on the last step. You can edit, reorder, or delete collections later from Your Collections.",
  },
  {
    question: "How do I start a practice session?",
    answer:
      "On Your Collections, click Start on a collection. Choose speed, shuffle, a timer or the full collection, and whether students type answers. Items then play full screen. Back leaves without saving a report. When a session finishes, you will see a short session report.",
  },
  {
    question: "What is interactive mode?",
    answer:
      "In interactive mode, students type the answer for each item. A wrong answer counts as missed, and they stay on the item until they get it right or skip it. Skip counts as skipped. In auto-play, items advance on their own and you can pause, go next, or go back.",
  },
  {
    question: "How do I turn the guided tour off?",
    answer:
      "Open Settings and uncheck Enable Guided Tour. Start Tour will hide from the menu. Check it again when you want the tours back, then click Start Tour on a page for a walkthrough.",
  },
  {
    question: "How does the name spinner work?",
    answer:
      "On the Name Generator page, add names with the + button, the name box, or by clicking the wheel. Spin picks a name. Uncheck a name to take it off the wheel without deleting it. You can change slice colors, turn sound on or off, and put names back after they are picked.",
  },
  {
    question: "How do the games work?",
    answer:
      "Games are extra practice modes. Timed Challenges and Timed Trials use collections you already made. Other games, like Typing Simulator, Missing Number, Matching, and Multiple Choice, let you use built-in examples or make your own. You can remove example passages or number patterns from those lists if you do not want them.",
  },
  {
    question: "Can I share a collection with other teachers?",
    answer:
      "When you create a collection, you can mark it public. Public collections appear on Discover Collections, where others can preview them and add a copy to their own account. Leave it private if only you should see it.",
  },
];

const UPCOMING_VIDEOS: InstructionalVideo[] = [
  {
    title: "Your Collections",
    description:
      "Find, start, edit, and manage the collections on your home page.",
  },
  {
    title: "New Collection & Setup",
    description:
      "Create a collection from scratch — name it, pick a type, generate or add items, and save.",
  },
  {
    title: "Practice Sessions",
    description:
      "Start a collection full screen, set speed and modes, and run a practice session.",
  },
  {
    title: "Discover Collections",
    description:
      "Browse public collections and add a copy to your account.",
  },
  {
    title: "Games",
    description:
      "Overview of the Games hub and how the practice game modes work.",
  },
  {
    title: "Quiz Show",
    description:
      "Host or join a live Quiz Show room with a class.",
  },
  {
    title: "Name Generator",
    description:
      "Build a name list, spin the wheel, and use sound and color options.",
  },
  {
    title: "Reports",
    description:
      "Read session reports, charts, and progress over time.",
  },
  {
    title: "Badges & Achievements",
    description:
      "See earned badges and how achievements unlock.",
  },
  {
    title: "Milestones",
    description:
      "Track classroom milestones and student progress toward goals.",
  },
  {
    title: "Settings",
    description:
      "Themes, fonts, guided tours, and other preferences.",
  },
  {
    title: "Shop & Licenses",
    description:
      "Add a premium license to your cart and complete checkout.",
  },
];

const Resources: React.FC = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [videos, setVideos] = useState<InstructionalVideo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);

  const steps = tourStepsResources();

  const startTour = () => {
    const tourCompleted = localStorage.getItem("tourCompleted");
    if (!tourCompleted) {
      setIsTourRunning(true);
      setCurrentTourStep(0);
    }
  };

  useEffect(() => {
    startTour();
  }, []);

  const handleTourStepChange = (step: number) => {
    setCurrentTourStep(step);
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted", "true");
  };

  useEffect(() => {
    const loadData = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setFaqs(FAQS);
      setVideos(UPCOMING_VIDEOS);
      setIsLoading(false);
    };

    loadData();
  }, []);

  return (
    <div className="page-container text-theme-text">
      <h1 className="inherit mb-8 text-3xl font-bold">
        <BubbleText>Resources</BubbleText>
      </h1>

      {isLoading ? (
        <p className="inherit">Loading resources...</p>
      ) : (
        <>
          <section className="faq-section mb-8 w-full max-w-3xl">
            <h2 className="inherit mb-4 text-2xl font-semibold">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div key={faq.question} className="inherit rounded border p-4">
                  <h3 className="inherit mb-2 text-xl font-medium">
                    {faq.question}
                  </h3>
                  <p className="inherit leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="videos-section w-full max-w-3xl">
            <h2 className="inherit mb-4 text-2xl font-semibold">
              Instructional Videos
            </h2>
            <p className="inherit mb-6 text-sm opacity-80">
              Short walkthroughs are on the way. Titles below show what is
              planned.
            </p>
            <div className="space-y-6">
              {videos.map((video) => (
                <div key={video.title} className="inherit">
                  <h3 className="inherit mb-2 text-xl font-medium">
                    {video.title}
                  </h3>
                  <p className="inherit">{video.description}</p>
                  {video.videoUrl ? (
                    <a
                      href={video.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block py-2 text-base text-blue-500 underline hover:text-blue-600"
                    >
                      Watch Video
                    </a>
                  ) : (
                    <p className="inline-block py-2 text-base font-medium text-gray-500">
                      Coming soon
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      <GuidedTour
        steps={steps}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={handleTourStepChange}
        tourName="resources"
      />
    </div>
  );
};

export default Resources;
